/* eslint-disable no-unreachable */
/* eslint-disable no-unused-vars */
const Autodesk = window.Autodesk;
const ForgePotree = window.ForgePotree;
import store from '@/store';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { getAllLeafComponents, pointerToRaycaster, getBBBox, getFragIdFromDbId, setPermeativeElementsTheme, setModelVisibility, isolateForge, clearThemingColors, setModelTransparency, getLeafsObjectsAsync } from '../utils/forgeUtils'
import { DisplaySettings, ForgeEquipment } from './../scripts/js/forgeModels';
import { addEmptyModelToViewer, getEmptyModelUrn, restoreStateToViewer3d } from './../scripts/js/forgeviewer';
import { forgeFetch } from '@/utils/mongoUtils'
const THREE = window.THREE;
import { ModelBrowserItem } from './../scripts/js/forgeModels';
import { $has, executeWhen } from '@/utils';
import permissions from '@Share/config/permissions';
import { intersectFirstRecursively } from '@/utils/PointCloudIntersection/pointCloudIntersection';
import { Renderer } from "./../../public/pointCloud/PotreeRenderer.js";
import HttpService from '@/utils/httpService';
import FPSMonitorOverlay from './FPSMonitorOverlay';

// Cache for point cloud settings to skip material updates when unchanged
var _prevPCSettings = null;
var _cameraDebounceTimer = null;
const POINT_CLOUD_LOAD_TIMEOUT_MS = 30000;

class MainExtension extends Autodesk.Viewing.Extension {
    constructor(loadedViewer, options) {
        super(loadedViewer, options);
        this.viewer = loadedViewer;

        this.mappedMaterial = {};
        this.tagsMapped = false;
        this._timer = null;
        this._pointclouds = new Map();
        this._pointCloudGroups = new Map();
        this._pointCloudAppearanceOverrides = new Map();
        this._boundUpdatePointClouds = this.updatePointClouds.bind(this);
        // ── Point Cloud Performance State ──────────────────────────────
        // WHY: Point cloud rendering is GPU-intensive and varies wildly across hardware.
        // These fields support adaptive rendering: rAF-based loading watcher (Phase 1),
        // frame-time monitoring, adaptive budget adjustment, GPU tier detection,
        // and user-controlled quality modes (Max Detail vs. Smooth, Boost toggle).
        // See: FPSMonitorOverlay.js for the user-facing HUD.

        // Loading watcher — replaces blind 500ms setInterval with rAF-driven updates
        this._loadingWatcherRafId = null;
        this._lastUpdateTime = 0;
        this._boundTickLoadingWatcher = this._tickLoadingWatcher.bind(this);

        // Adaptive GPU budget — monitors frame times and adjusts pointBudget to
        // maintain 30-50fps. User's slider value is the ceiling (never exceeded).
        this._frameTimes = [];          // rolling 60-frame window of frame intervals (ms)
        this._lastTickTime = 0;         // timestamp of previous rAF tick
        this._adaptiveBudget = null;    // null = use user slider as-is; number = adaptive cap
        this._lastAdaptiveCheck = 0;    // last time budget was adjusted (1s cooldown)
        this._loadingIdleSince = 0;     // when numNodesLoading last became 0 (settle detection)
        this._lastBudgetReduction = 0;  // when budget was last reduced (5s freeze on increases)

        // User quality controls (toggled via FPS overlay)
        this._maxDetailMode = true;     // true = never reduce budget during loading (accepts temporary lag)
        this._boostMode = false;        // true = 2× download concurrency + GPU upload rate
        this._gpuTier = null;           // cached { tier, renderer, downloads, gpuPerFrame }
        this.initFocalLength;
        this.isMovingScaffold = false;
        this.isScalingScaffold = false;
        this.modelLoaded = false;
        this.pinClassName = 'pinned-point-3d'

        var _this = this;
        this.viewer.registerContextMenuCallback( 'reality-context-menu', ( menu, status ) => {
            let showAllObjects = menu.find(o=>o.title === 'Show All Objects');

            if (showAllObjects) {
                let original = showAllObjects.target;
                showAllObjects.target = () => {
                    this.endIsolation();
                    original();
                }
            }

            if(status.hasSelected) {
                menu.push({
                    title: 'Crop Selected',
                    target: () => {
                        _this.cropSelectedElements();
                    }
                });

                const allowAdd = $has([
                    permissions.Tags.Container.Create,
                    permissions.Tags.Equipment.Create,
                    permissions.Tags.General.Create,
                    permissions.Tags.Permit.Create,
                    permissions.Tags.Pipeline.Create,
                    permissions.Tags.Scaffold.Create,
                    permissions.Tags.Structural.Create,
                    permissions.InspectionTags.Create
                ], true)

                if (allowAdd) {
                    menu.push({
                        title: 'Add Tag',
                        target: () => {
                            let center = getBBBox(_this.viewer.model.selector.getSelection()).getCenter()
                            let point = store.state.forgeService.view3dToWorld(center)
                            store.state.forgeService.addNewTag("3d",point,null,true);
                        }
                    });
                }

            }
            if(store.state.mouseDownHitTest&&store.state.mouseDownHitTest.point){
                var closestImage = store.state.forgeService.getClosestImageToPoint(store.state.mouseDownHitTest.point);
                menu.push({
                    title: 'Nearest 360',
                    target: () => {
                        store.state.forgeService.openNearest360ToPoint(store.state.mouseDownHitTest.point, (isImageLoaded) => {
                            let isLinkedViewers = store.state.areViewsLocked

                            if (isLinkedViewers) {
                                store.state.forgeService.zoomToPointIn3DAnd360(store.state.mouseDownHitTest.point);
                            } else {
                                store.state.forgeService.zoomToPointIn360Viewer(store.state.mouseDownHitTest.point);
                                store.state.forgeService.zoomToPoint3dViewer(store.state.mouseDownHitTest.point);
                            }
                        });
                    }
                });
            }

            // Add measurement option if crop box exists and is active
            const cropExtension = _this.viewer.getExtension('Cropping3DExtension');
            if (cropExtension && cropExtension.cropBox && cropExtension.croppingOn) {
                // Check if there are visible point clouds
                const hasVisiblePC = _this._pointCloudGroups &&
                    Array.from(_this._pointCloudGroups.values()).some(group => group.visible);

                if (hasVisiblePC) {
                    menu.push({
                        title: 'Measure Cropped Region',
                        target: () => {
                            cropExtension.openMeasurementDialog();
                        }
                    });
                }
            }

        });

    }

    load() {
        debugLog(`%c MainExtension loaded`, `color: green; font-size: 16px;`);

        var _this = this;
        this.viewer.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => {
            store.commit("setIsTreeLoaded", true);
            this.initFocalLength = this.viewer.getFocalLength();
            this.syncTagsToViewer();
            this.updatePinPoint();
        }, { once: true });

        this.viewer.addEventListener(Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, () => {
            store.state.model3dId = this.viewer.model.id

            if (store.state.fetchedState&&store.state.fetchedState.viewer3DSettings)
                restoreStateToViewer3d();
            else if(store.state.forgeService.image360ToNavigateToIn3D){
                store.state.forgeService.navigateIn3D(store.state.forgeService.image360ToNavigateToIn3D);
                store.state.forgeService.image360ToNavigateToIn3D = null;
            }
            else if (store.state.forgeService.modelToNavigateToIn3D) {
                store.state.forgeService.toggleShowingModel(store.state.forgeService.modelToNavigateToIn3D);
                store.state.forgeService.modelToNavigateToIn3D = null
            }
            else if (store.state.forgeService.cameraToNavigateToIn3D) {
                store.state.forgeService.cameraToNavigateToIn3D.load();
                store.state.forgeService.cameraToNavigateToIn3D = null
            }
            else if (store.state.selectedImage) {
                store.state.forgeService.navigateIn3D(store.state.selectedImage);
            }

            if (store.state.forgeService.pointCloudsToShowIn3D){
                for (let i = 0; i < store.state.forgeService.pointCloudsToShowIn3D.length; i++) {
                    const pointCloud = store.state.forgeService.pointCloudsToShowIn3D[i];
                    store.state.forgeService.toggleShowingPointCloudIn3D(pointCloud);
                }
                store.state.forgeService.pointCloudsToShowIn3D = []
            }

        }, { once: true });

        this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, (e) => {
            if(!_this.viewer||!_this.viewer.model)
                return;

            // IMMEDIATE — must stay per-frame
            store.state.forgeService.toggleCrossHairCursorToViewer(!!store.state.isPickingPin || !!store.state.isPickingCmlPoint, this.viewer)
            store.state.forgeService.updateLabelsPositionsByClassName(this.pinClassName, this.viewer)
            // immediate ops done (cursor, labels)

            // IMMEDIATE — pano sync must stay per-frame for smooth linked-views experience
            if (!(store.state.forgeService.startedPanoChange
                || store.state.isPickingCmlPoint
                || !store.state.selectedImage
                || !store.state.selectedImage.isLocked
                || !store.state.areViewsLocked)) {

                var _panoPositionsDiffer = false;
                if (store.state.selectedImage.forgeCameraPosition) {
                    var currentPosition = _this.viewer.navigation.getPosition();
                    var viewerCamPosition = store.state.selectedImage.getCameraPositionInViewer();
                    if (viewerCamPosition.x != currentPosition.x
                        || viewerCamPosition.y != currentPosition.y
                        || viewerCamPosition.z != currentPosition.z) {
                            _panoPositionsDiffer = true;
                            store.state.areViewsLocked = false;
                            store.state.forgeService.linkViewers();
                    }
                }
                // Sync pano when: no forgeCameraPosition, OR positions already match
                if (!_panoPositionsDiffer) {
                    store.state.forgeService.updatePanoViewerFromForge(store.state.selectedImage);
                }
            }

            // DEBOUNCED — store position + plan direction don't need per-frame precision
            clearTimeout(_cameraDebounceTimer);
            _cameraDebounceTimer = setTimeout(() => {
                if(!_this.viewer||!_this.viewer.model) return;
                // debounced ops fired (store + 2D direction)

                let modelCameraPostion = store.state.forgeService.view3dToWorld(e.camera.position);
                store.commit("setCamera3dPosition", modelCameraPostion)

                //update plan 2D direction
                var planExtension = store.state.forgeService.getDrawPointsExtension();
                if (planExtension)
                    planExtension.drawDirection2D();
            }, 100);
        });

        this.viewer.addEventListener(Autodesk.Viewing.CUTPLANES_CHANGE_EVENT, (ev) => {
            store.state.cropBoxApplied = !!ev.planes
        });

        // this.viewer.addEventListener(Autodesk.Viewing.CUTPLANES_CHANGE_EVENT, () => {
        //     if(!_this._pointclouds)
        //         return;
        //     setTimeout(() => {

        //         var pointclouds = Array.from(_this._pointclouds.values());
        //         if (pointclouds.length == 0)
        //             return;
        //         let extension = _this.viewer.getExtension('Autodesk.Section');
        //         if (!extension || !extension.isActive())
        //             return;
        //         let cropBox = null;
        //         let planes = extension.getSectionPlanes(true);
        //         if (!planes || planes.length == 0)
        //             return;
        //         if (extension.mode == 'box') {
        //             if (planes.length == 6) {
        //                 let min = new THREE.Vector3(planes[3].w, planes[4].w, planes[5].w);
        //                 let max = new THREE.Vector3(-planes[0].w, -planes[1].w, -planes[2].w);
        //                 cropBox = new THREE.Box3(min, max);
        //             }
        //         } else if (extension.mode == 'x') {
        //             let min = new THREE.Vector3(planes[0].w, -Infinity, -Infinity);
        //             let max = new THREE.Vector3(Infinity, Infinity, Infinity);
        //             cropBox = new THREE.Box3(min, max);
        //         } else if (extension.mode == 'y') {
        //             let min = new THREE.Vector3(-Infinity, planes[0].w, -Infinity);
        //             let max = new THREE.Vector3(Infinity, Infinity, Infinity);
        //             cropBox = new THREE.Box3(min, max);
        //         } else if (extension.mode == 'z') {
        //             let min = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        //             let max = new THREE.Vector3(Infinity, Infinity, -planes[0].w);
        //             cropBox = new THREE.Box3(min, max);
        //         }

        //         pointclouds.forEach(o => o.setClippingBoxNew(cropBox));
        //         //drawBoxLines(cropBox)
        //     }, 100);

        // });

        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, (event) => {
            const normalizeUrn = value => String(value || '')
                .replace(/^urn:/i, '')
                .replace(/=+$/, '');
            const loadedUrn = normalizeUrn(event.model?.getSeedUrn?.());
            if (loadedUrn && loadedUrn !== normalizeUrn(getEmptyModelUrn())) {
                addEmptyModelToViewer()
                    .then(() => {
                        debugLog(`%c Empty model added to viewer`, `color: green; font-size: 16px;`);
                    })
                    .catch(err => debugWarn('Empty model load failed', err));
            }
        }, { once: true });

        this.viewer.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, (event) =>
        {
            let bounding = this.viewer.model.getBoundingBox();

            let unitScale = store.state.unitScale ?? 1

            let isLargeModel = Math.abs(bounding.max.x) * unitScale > 1500 / unitScale || Math.abs(bounding.max.y) * unitScale > 1500 / unitScale

            store.commit('setIsLargeModel', isLargeModel);

            if (isLargeModel) this.viewer.impl.setNearRadius(0);

            store.state.forgeService.updateAll3DIcons();

            let model = event.model;

            let fps = store.state.userSettings.displaySettings.renderFPS ?? 8;
            this.viewer.impl.setFPSTargets(undefined, fps, fps);

            // let modelSize = model.getGeometryList().geomMemory / (1024 * 1024);
            // if ((modelSize > 150 && modelSize < 300) || store.state.dbidsToLoad) {
            //     this.viewer.impl.setFPSTargets(undefined, 10, 10)
            // } else if (modelSize > 300) {
            //     this.viewer.impl.setFPSTargets(undefined, 5, 5)
            // }
            let urn = model.getSeedUrn();
            let smallModel = store.state.uploadedModels.find(o=>o.urn == urn);
            if (smallModel && !store.state.forgeService.smallModelsUrnsPerElements.get(smallModel.urn)) {
                debugLog(`%c Loading elements for small model ${smallModel.urn}`, 'color: #ff6600; font-weight: bold; font-size: 16px;');
                // let dbIds = [...new Set(viewer.model.getFragmentList().fragments.fragId2dbId)];
                getAllLeafComponents(model,(dbIds) => {
                    //add data to elements
                    var elements = [];
                    for (var i = 0; i < dbIds.length; i++) {
                        let elem = store.state.forgeService.dbidToForgeObject(dbIds[i], model);
                        if (!elem) continue; // dbidToForgeObject returns null when no usable unit scale
                        elem.urn = smallModel.urn;
                        elements.push(elem);
                    }
                    store.state.forgeService.smallModelsUrnsPerElements.set(smallModel.urn, elements);
                });
            }

            if(store.state.forgeService.tagToZoomToIn3D){
                executeWhen(
                    () => !!this.viewer?.model?.getInstanceTree(),
                    () => {
                        const tag = store.state.forgeService.tagToZoomToIn3D;
                        const shouldIsolate = store.state.forgeService.isolateOnZoom ?? true;
                        store.state.forgeService.zoomInAllAvailableViewers(tag, shouldIsolate);
                        store.state.forgeService.tagToZoomToIn3D = null;
                        store.state.forgeService.isolateOnZoom = true; // Reset to default
                    },
                    10,
                )
            }

            _this.modelLoaded = true;

            store.commit('setIsGeometryLoaded', true);

            // store.state.forgeService.updateModelGray();


            if (store.state.forgeService.on3dModelLoadedCallback) {
                store.state.forgeService.on3dModelLoadedCallback()
                store.state.forgeService.on3dModelLoadedCallback = null;
            }
        }, { once: true });

        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_ISOLATION_CHANGED_EVENT, (event) => {
            let settings = store.state.userSettings?.displaySettings || new DisplaySettings();
            if (!settings.showMasterModel) {
                this.viewer.model.setAllVisibility(0);
            }

            if (store.getters['getIsLargeModel'] && !!event.isolation.length) {
                let unitScale = store.state.unitScale ?? 1;
                let maxLength = 100 / unitScale;
                let radius = 0;

                if (event.isolation[0].ids.length) {
                    let { ids, model } = event.isolation[0]
                    let bBox = getBBBox(ids, model);

                    // Check infinite bounding box
                    if (bBox.min.x == Infinity || bBox.min.y == Infinity || bBox.min.z == Infinity || bBox.max.x == -Infinity || bBox.max.y == -Infinity || bBox.max.z == -Infinity) {
                        return;
                    }

                    let {x, y, z} = bBox.getSize();
                    if (x < maxLength && y < maxLength && z < maxLength) {
                        radius = 0.15;
                    }
                }

                this.viewer.impl.setNearRadius(radius);
            }

            // clear the stored themed dbids if not toggle ghosting ON in the solid mode
            if (!event.isolation.length &&
                (!store.state.isGhostingOn || (store.state.isGhostingOn&&store.state.isGhostingWireframe))) {
                let themedDbIds = store.getters['GET_THEMED_DB_IDS'];
                if (themedDbIds.length) {
                    setPermeativeElementsTheme(themedDbIds, null);
                    store.commit('SET_THEMED_DB_IDS',[]);
                }
            }
        })

        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, (event) => {
            if (event.selections.length == 1) {
                let perModel = event.selections;
                let singleSelectionEvent = {
                    type: Autodesk.Viewing.SELECTION_CHANGED_EVENT,
                    fragIdsArray: perModel[0] ? perModel[0].fragIdsArray : [],
                    dbIdArray: perModel[0] ? perModel[0].dbIdArray : [],
                    nodeArray: perModel[0] ? perModel[0].dbIdArray : [],
                    model: perModel[0].model,
                    mouseButton: event.mouseButton,
                };
                this.viewer.dispatchEvent(singleSelectionEvent);
            }
        });

        this.viewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event) =>
        {
            // Cancel open any popup if we already clicked on 3D tag.
            if (store.state.forgeService.getLabels3DExtension()?.hovered !== -1) return;
            if (store.getters['getIgnoreModelSelectionEvent']) return;

            let selection = this.viewer.model.selector.getSelection();
            store.commit("setSelectedDbids", selection)

            let addingTagInfo = store.getters['GET_ADDING_TAG_INFO'];
            if (addingTagInfo) {
                store.commit("SET_ADDING_TAG_INFO", { ...addingTagInfo, selection: selection })
            }
            if (selection.length == 0) return;

            if (store.state.isPickingTag) {
                store.state.forgeService.createTagFromSelection();
                return;
            }

            if (store.getters["getModelElementsObject"] instanceof ModelBrowserItem) {
                store.dispatch('updateSelectedNode', { nodeId: selection[0] })
            }
        });

        this.viewer.addEventListener(Autodesk.Viewing.ISOLATE_EVENT, () =>
        {
            let isolated = this.viewer.getIsolatedNodes();
            store.commit("setIsolatedNodes", isolated)
        });

        return true;
    }
    onToolbarCreated() {
    }
    getPointCloudByName(name) {
        if (!this._pointclouds) return null;
        if (!this._pointclouds.has(name)) return null;
        return this._pointclouds.get(name);
    }
    /**
     * Toggle a loaded point cloud without unloading its octree.
     *
     * Verification uses this for rapid compare on/off. Global callers keep
     * using showPointCloud(name, false), whose established contract is a full
     * disposal. Returning false means the cloud has not been loaded yet.
     */
    setPointCloudVisibility(cloudName, visible) {
        const group = this._pointCloudGroups?.get(cloudName);
        if (!group) return false;

        group.visible = !!visible;
        const pointcloud = this._pointclouds?.get(cloudName);
        if (pointcloud) pointcloud.visible = !!visible;
        this.viewer?.impl?.invalidate?.(true, true, true);
        return true;
    }
    getAutomaticTagForSelection(dbids, callback){
        let tag = store.state.tags.find(o => o.children.some(o => o.dbId == dbids[0]));
        if (tag)
            callback(tag);
        else {
            let autoTags = store.getters['GET_AUTOMATIC_TAGS']
            tag = autoTags.find(o => o.children.some(o => o.dbId == dbids[0]));
            if (tag)
                callback(tag);
            else {
                var containingAutomaticTagId = this.getFirstContainingAutomaticTagId(dbids[0])
                if (containingAutomaticTagId) {
                    forgeFetch(`api/equipments/getAutomaticTagById?id=${containingAutomaticTagId}`, autoTag => {
                        if (autoTag) {
                            autoTag = Object.assign(new ForgeEquipment(), autoTag)
                            autoTag.parseContent()
                            autoTags.push(autoTag)
                            callback(autoTag);
                        }
                    })
                }
            }

        }
    }
    getFirstContainingAutomaticTagId(dbid){
        let existing = store.state.TAGS.allExistingAutomaticDbids.find(o =>
            o.children?.some(c => c.dbId == dbid)
        )
        if(existing)
            return existing.id
        var parentId = this.viewer.model.getInstanceTree()?.getNodeParentId(dbid) ?? 0
        if(parentId == 0)
            return null
        return this.getFirstContainingAutomaticTagId(parentId)
    }
    getFirstContainingAMISTagId(dbId){

        const set = store.getters.GET_ALL_AMIS_TAGS_IDS;

        let existing = set.find(t => t.children.some(c => c.dbId == dbId));

        if(existing) return existing.id

        var parentId = this.viewer.model.getInstanceTree()?.getNodeParentId(dbId)

        if(parentId == 0) return null;

        return this.getFirstContainingAMISTagId(parentId)

    }
    fitViewToPointCloudBoundary(viewer, pointCloud) {
        let d = pointCloud.boundingBox.max.sub(pointCloud.boundingBox.min);

        let offset = (1 / store.state.unitScale) ;

        let min = new THREE.Vector3(pointCloud.position.x - (offset * d.x), pointCloud.position.y - (offset * d.y), pointCloud.position.z - (offset * d.z));
        let max = new THREE.Vector3(pointCloud.position.x + (offset * d.x), pointCloud.position.y + (offset * d.y), pointCloud.position.z + (offset * d.z));

        var box = new THREE.Box3(min, max);
        viewer.navigation.fitBounds(true, box);
    }

    //point cloud
    showPointCloud(cloudName, show, url, fitToView = false) {
        return new Promise((resolve, reject) => {
            if (store.state.mainApp) {
                store.state.mainApp.showPanoMeasureTool = store.state.pointClouds.some(o => o.isShown);
            }

            if (!this._pointCloudGroups) this._pointCloudGroups = new Map();

            if (this._pointCloudGroups.has(cloudName)) {

                if (show) {
                    this._pointCloudGroups.get(cloudName).visible = true;

                    let pc = this._pointclouds.get(cloudName)

                    if (pc) {
                        pc.setClippingBoxNew(store.state.forgeService.getCropping3DExtension().cropBox)
                    }

                    this.viewer.impl.invalidate(true, true, true);

                    setTimeout(() => {
                        store.commit("setShowLoading", false);
                    }, 200)

                    resolve(pc);
                } else {
                    // Fully dispose to free GPU memory and LRU cache
                    this.disposePointCloudByName(cloudName);

                    setTimeout(() => {
                        store.commit("setShowLoading", false);
                    }, 200)

                    resolve(null);
                }

                return;
            }

            if (!show) {
                setTimeout(() => {
                    store.commit("setShowLoading", false);
                }, 200)

                resolve(null);
                return;
            }

            var group = new THREE.Group();

            this._pointCloudGroups.set(cloudName, group);

            if (!this.viewer.overlays.hasScene('potree-scene')) {

                this.viewer.overlays.addScene('potree-scene');
            }

            this.viewer.overlays.addMesh(group, 'potree-scene');

            if (!this._pointclouds) this._pointclouds = new Map();

            // Register camera listener once (P1: setInterval removed — replaced by loading-aware rAF watcher)
            if (!this._timer) {
                this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this._boundUpdatePointClouds);
                this._timer = true; // flag: camera listener is registered
            }

            this.loadPointCloud(group, cloudName, url.replace(/%2F/gi, "/")).then(pc => {
                resolve(pc);

                setTimeout(() => {
                    store.commit("setShowLoading", false);
                }, 200)

                if (fitToView) {
                    this.fitViewToPointCloudBoundary(this.viewer, pc);
                }
            }).catch(err => {
                try {
                    this.disposePointCloudByName(cloudName);
                } catch (cleanupErr) {
                    debugWarn(`Failed to clean up point cloud ${cloudName}`, cleanupErr);
                }
                store.commit("setShowLoading", false);
                reject(err);
            });
        });
    }
    async loadPointCloud(group, name, url) {

        if (this._pointclouds.has(name)) {
            return Promise.resolve(this._pointclouds.get(name));
        }

        // Fetch signed URLs and initialize PotreeSignedUrls (handles Azure SAS and S3)
        // Pass point cloud name to support multiple point clouds with separate signed URL managers
        const metadataUrl = await window.PotreeSignedUrls.fetchAndInit(url, HttpService, name);

        return new Promise((resolve, reject) => {
            let settled = false;
            const settle = (callback, value) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                callback(value);
            };
            const resolveLoad = pointcloud => settle(resolve, pointcloud);
            const rejectLoad = err => settle(reject, err);
            const disposeDetachedPointCloud = pointcloud => {
                if (ForgePotree.Global.lru && pointcloud?.pcoGeometry) {
                    ForgePotree.Global.lru.flushForPointCloud(pointcloud.pcoGeometry);
                }
                pointcloud?.dispose?.();
                pointcloud?.material?.dispose?.();
                pointcloud?.geometry?.dispose?.();
            };
            const timeoutId = setTimeout(
                () => rejectLoad(new Error(`Timed out loading point cloud ${name}.`)),
                POINT_CLOUD_LOAD_TIMEOUT_MS,
            );

            try {
                ForgePotree.loadPointCloud(metadataUrl, name, (ev) => {
                    try {
                        const pointcloud = ev?.pointcloud;
                        if (settled) {
                            if (this._pointclouds.get(name) === pointcloud) return;
                            disposeDetachedPointCloud(pointcloud);
                            if (this._pointCloudGroups?.get(name) !== group) {
                                window.PotreeSignedUrlManagers?.removePointCloud?.(name);
                            }
                            return;
                        }
                        if (!pointcloud) {
                            throw ev?.error instanceof Error
                                ? ev.error
                                : new Error(`Potree failed to load point cloud ${name}.`);
                        }

                        // The user may switch scans or leave Verification while this
                        // asynchronous load is still in flight. Disposal removes the
                        // registered group; never reattach the completed octree to a
                        // group the viewer no longer owns.
                        if (this._pointCloudGroups?.get(name) !== group) {
                            disposeDetachedPointCloud(pointcloud);
                            window.PotreeSignedUrlManagers?.removePointCloud?.(name);
                            resolveLoad(null);
                            return;
                        }

                        pointcloud.receiveShadow = true;
                        pointcloud.castShadow = true;

                        const { material } = pointcloud;
                        let scaleFactor = 1 / store.state.unitScale;
                        pointcloud.position.multiplyScalar(scaleFactor).sub(store.state.globalOffset);
                        pointcloud.scale.copy(new THREE.Vector3(scaleFactor, scaleFactor, scaleFactor));

                        // Save the original bounding box before applying transformations
                        if (pointcloud.boundingBox) {
                            pointcloud.originalBoundingBox = pointcloud.boundingBox.clone();

                            // Calculate min and max elevation for point cloud display
                            const boundingBoxSize = pointcloud.boundingBox.getSize(new THREE.Vector3());
                            pointcloud.minPointElevation = pointcloud.boundingBox.min.z;
                            pointcloud.maxPointElevation = pointcloud.boundingBox.max.z;

                            // Store the dimensions for later use
                            pointcloud.dimensions = {
                                width: boundingBoxSize.x,
                                height: boundingBoxSize.y,
                                depth: boundingBoxSize.z
                            };

                            debugLog(`Point cloud loaded: ${name}`);
                            debugLog(`Min elevation: ${pointcloud.minPointElevation}, Max elevation: ${pointcloud.maxPointElevation}`);
                        }

                        material.size = 6.0;
                        material.pointColorType = ForgePotree.PointColorType.RGB; //RGB | DEPTH | HEIGHT | POINT_INDEX | LOD | CLASSIFICATION | INTENSITY_GRADIENT
                        material.pointSizeType = ForgePotree.PointSizeType.FIXED; //ADAPTIVE | FIXED
                        material.shape = ForgePotree.PointShape.SQUARE; //CIRCLE | SQUARE | PARABOLOID

                        group.add(pointcloud);
                        this._pointclouds.set(name, pointcloud);
                        // Store URL for accurate lookup when multiple point clouds have the same name
                        pointcloud.sourceUrl = url;
                        this._updatePointLoadLimit(); // P6: scale VRAM budget to actual scan sizes
                        this._startLoadingWatcher();  // P1: kick off rAF watcher for progressive loading
                        if (!this._fpsMonitor) {
                            var self = this;
                            this._fpsMonitor = new FPSMonitorOverlay(this.viewer.container, function(isMaxDetail) {
                                self._maxDetailMode = isMaxDetail;
                                self._frameTimes = [];
                                self._loadingIdleSince = 0;
                                debugLog('[P10] Mode: ' + (isMaxDetail ? 'Max Detail' : 'Smooth'));
                            }, function(isBoosted) {
                                self._boostMode = isBoosted;
                                self._applyLoadingSpeed();
                                debugLog('[P10] Boost: ' + (isBoosted ? 'ON' : 'OFF'));
                            });
                        }
                        this._applyLoadingSpeed(); // P10: set initial loading speed based on GPU tier
                        if (this._fpsMonitor && this._gpuTier) {
                            this._fpsMonitor.setGPUTier(this._gpuTier.tier, this._gpuTier.renderer);
                        }
                        _prevPCSettings = null; // Force full settings application for newly loaded cloud
                        this.updatePointClouds();
                        pointcloud.setClippingBoxNew(store.state.forgeService.getCropping3DExtension().cropBox)
                        resolveLoad(pointcloud);
                    } catch (err) {
                        rejectLoad(err);
                    }
                });
            } catch (err) {
                rejectLoad(err);
            }
        });
    }
    refreshPointCloudsByName(name) {
        debugLog(`Refreshing point cloud ${name}`)
        if (!this._pointCloudGroups) return;
        if (!this._pointCloudGroups.has(name)) return;
        const group = this._pointCloudGroups.get(name);
        group.visible = false;
        group.traverse(child => {
            if (child.geometry) {
                child.geometry.dispose();
            }
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(material => material.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
        this.viewer.overlays.removeMesh(group, 'potree-scene');
        this.viewer.impl.invalidate(true, true, true);
        this._pointCloudGroups.delete(name);
        const pc = this._pointclouds.get(name);
        if (pc) {
            if (ForgePotree.Global.lru) {
                ForgePotree.Global.lru.flushForPointCloud(pc.pcoGeometry);
            }
            if (pc.dispose) pc.dispose();
            if (pc.material) pc.material.dispose();
            if (pc.geometry) pc.geometry.dispose();
        }
        this._pointclouds.delete(name);

        // Cleanup signed URL manager for this point cloud before reloading
        if (window.PotreeSignedUrlManagers) {
            window.PotreeSignedUrlManagers.removePointCloud(name);
        }

        this.showPointCloud(name, true, store.state.pointClouds.find(o => o.isShown && o.name == name)?.url);
    }
    disposePointCloudByName(name) {
        debugLog(`Disposing point cloud ${name}`)
        if (!this._pointCloudGroups) return;

        if (this._pointCloudGroups.has(name)) {
            const group = this._pointCloudGroups.get(name);
            group.visible = false;
            group.traverse(child => {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
            this.viewer.overlays.removeMesh(group, 'potree-scene');
            this.viewer.impl.invalidate(true, true, true);
            this._pointCloudGroups.delete(name);
        }

        if (this._pointclouds.has(name)) {
            const pc = this._pointclouds.get(name);
            if (ForgePotree.Global.lru) {
                ForgePotree.Global.lru.flushForPointCloud(pc.pcoGeometry);
            }
            if (pc.dispose) pc.dispose();
            if (pc.material) pc.material.dispose();
            if (pc.geometry) pc.geometry.dispose();
            this._pointclouds.delete(name);
        }

        // Clean up listener/watcher when last cloud is disposed
        if (this._pointclouds && this._pointclouds.size === 0 && this._timer) {
            if (this._loadingWatcherRafId) {
                cancelAnimationFrame(this._loadingWatcherRafId);
                this._loadingWatcherRafId = null;
            }
            this._timer = null;
            if (this._fpsMonitor) { this._fpsMonitor.destroy(); this._fpsMonitor = null; }
            this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this._boundUpdatePointClouds);
        } else if (this._pointclouds && this._pointclouds.size > 0) {
            this._updatePointLoadLimit(); // P6: recalculate after cloud removed
        }

        // Cleanup signed URL manager for this point cloud
        if (window.PotreeSignedUrlManagers) {
            window.PotreeSignedUrlManagers.removePointCloud(name);
        }

        const entry = store.state.pointClouds.find(o => o.name == name);
        if (entry) entry.isShown = false;
    }
    hideAllPointClouds() {
        if (!this._pointCloudGroups) return;

        store.state.pointClouds.forEach(o => o.isShown = false);

        this._pointCloudGroups.forEach(group => {
            group.visible = false;
            // Dispose of geometries and materials
            group.traverse(child => {
                if (child.geometry) {
                    child.geometry.dispose();
                }
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            });
            this.viewer.overlays.removeMesh(group, 'potree-scene');
            this.viewer.impl.invalidate(true, true, true);
        });

        // Clear point cloud data structures
        this._pointclouds.forEach(pc => {
            if (ForgePotree.Global.lru) {
                ForgePotree.Global.lru.flushForPointCloud(pc.pcoGeometry);
            }
            if (pc.dispose) pc.dispose();
            if (pc.material) pc.material.dispose();
            if (pc.geometry) pc.geometry.dispose();
        });

        this._pointCloudGroups.clear();
        this._pointclouds.clear();

        if (this._timer) {
            if (this._loadingWatcherRafId) {
                cancelAnimationFrame(this._loadingWatcherRafId);
                this._loadingWatcherRafId = null;
            }
            this._timer = null;
            if (this._fpsMonitor) { this._fpsMonitor.destroy(); this._fpsMonitor = null; }
            this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this._boundUpdatePointClouds);
        }

        if (store.state.mainApp) {
            store.state.mainApp.showPanoMeasureTool = false;
            store.commit("setShowLoading", false);
        }

        // Force a garbage collection hint
        if (window.gc) window.gc();

        this.viewer.impl.invalidate(true, true, true);
    }
    resetPointCloudSettingsCache() {
        _prevPCSettings = null;
    }
    /**
     * Apply a route-scoped appearance override to one point cloud.
     *
     * Stored even when the cloud is still loading; loadPointCloud ends by
     * calling updatePointClouds, which reapplies this override after the global
     * user settings. Verification can therefore configure a scan before its
     * octree exists without racing the async loader.
     */
    setPointCloudAppearance(cloudName, settings = {}) {
        if (!cloudName) return false;
        if (!this._pointCloudAppearanceOverrides) {
            this._pointCloudAppearanceOverrides = new Map();
        }
        this._pointCloudAppearanceOverrides.set(cloudName, { ...settings });
        return this._applyPointCloudAppearance(cloudName, settings);
    }
    clearPointCloudAppearance(cloudName) {
        if (!cloudName || !this._pointCloudAppearanceOverrides) return;
        this._pointCloudAppearanceOverrides.delete(cloudName);
        // Restore global point-cloud settings if the cloud remains loaded.
        _prevPCSettings = null;
        this.updatePointClouds();
    }
    _applyPointCloudAppearance(cloudName, settings = {}) {
        const pointcloud = this._pointclouds?.get(cloudName);
        const material = pointcloud?.material;
        if (!material) return false;

        const opacity = Number(settings.opacity);
        material.opacity = Number.isFinite(opacity)
            ? Math.min(1, Math.max(0.05, opacity))
            : 1;

        const pointSize = Number(settings.pointSize);
        material.size = Number.isFinite(pointSize)
            ? Math.min(12, Math.max(1, pointSize))
            : 3;
        material.shape = settings.pointShape === 'circle'
            ? ForgePotree.PointShape.CIRCLE
            : ForgePotree.PointShape.SQUARE;
        material.useEDL = !!settings.edl;

        if (settings.colorMode === 'solid') {
            material.pointColorType = ForgePotree.PointColorType.COLOR;
            material.color = new THREE.Color(settings.color || '#FFD166');
        } else if (settings.colorMode === 'height') {
            material.pointColorType = ForgePotree.PointColorType.HEIGHT;
        } else {
            material.pointColorType = ForgePotree.PointColorType.RGB;
        }

        this.viewer?.impl?.invalidate?.(true, true, true);
        return true;
    }
    getshownPointClouds(){
        let shownPointCloudsNames = store.state.pointClouds.filter(o=>o.isShown).map(o=>o.name);
        if(shownPointCloudsNames.length==0)
            return [];
        return shownPointCloudsNames.map(o => this._pointclouds.get(o)).filter(o => o);
    }
    updatePointClouds() {
        this._lastUpdateTime = performance.now(); // P1: used by rAF watcher to avoid double-firing
        if(this.viewer==null||this.viewer.impl==null||this.viewer.impl.camera==null)
            return;

        let { colorType, opacity, rgbBrightness, rgbContrast, rgbGamma, elevationMin, elevationMax, colorSchema, pointBudget, useEDL, pointSize, pointType, sizeType } = store.state.userSettings.displaySettings.pointCloudDisplay;

        var shownPointclouds = this.getshownPointClouds();
        // P10: adaptive budget uses user slider as ceiling, reduces when GPU struggles
        let userBudget = pointBudget || 1000000;
        // Reset adaptive budget when user changes the slider (re-adapt from new value)
        if (_prevPCSettings && _prevPCSettings.pointBudget !== pointBudget) {
            this._adaptiveBudget = null;
            this._frameTimes = [];
        }
        let totalBudget = this._adaptiveBudget != null ? Math.min(this._adaptiveBudget, userBudget) : userBudget;
        let perCloudBudget = shownPointclouds.length > 0 ? totalBudget / shownPointclouds.length : totalBudget;

        var pointclouds = Array.from(this._pointclouds.values());

        // Check if settings have changed
        var _settingsChanged = !_prevPCSettings
            || _prevPCSettings.colorType     !== colorType
            || _prevPCSettings.opacity       !== opacity
            || _prevPCSettings.rgbBrightness !== rgbBrightness
            || _prevPCSettings.rgbContrast   !== rgbContrast
            || _prevPCSettings.rgbGamma      !== rgbGamma
            || _prevPCSettings.elevationMin  !== elevationMin
            || _prevPCSettings.elevationMax  !== elevationMax
            || _prevPCSettings.colorSchema   !== colorSchema
            || _prevPCSettings.pointBudget   !== pointBudget
            || _prevPCSettings.useEDL        !== useEDL
            || _prevPCSettings.pointSize     !== pointSize
            || _prevPCSettings.pointType     !== pointType
            || _prevPCSettings.sizeType      !== sizeType;

        // Always update point budget and LOD threshold (depends on cloud count, not settings)
        pointclouds.forEach(pc => {
            pc.pointBudget = perCloudBudget;
            pc.minimumNodePixelSize = 10; // lower = deeper LOD loads from further away (default was 30)
        });

        // Only update material properties if settings changed
        if (_settingsChanged) {
            pointclouds.forEach(pc => {
                pc.material.size = pointSize ?? 8.0;
                pc.material.shape = pointType === 'Circle' ? ForgePotree.PointShape.CIRCLE : ForgePotree.PointShape.SQUARE;
                pc.material.useEDL = useEDL;
                pc.material.opacity = opacity / 100.0;

                // Properly set the point color type from user selection
                if (ForgePotree.PointColorType[colorType] !== undefined) {
                    pc.material.pointColorType = ForgePotree.PointColorType[colorType];
                } else {
                    debugWarn(`Invalid color type: ${colorType}, defaulting to RGB`);
                    pc.material.pointColorType = ForgePotree.PointColorType.RGB;
                }

                // Set point size type based on user settings (ADAPTIVE or FIXED)
                if (sizeType === 'Adaptive') {
                    pc.material.pointSizeType = ForgePotree.PointSizeType.ADAPTIVE;
                } else {
                    pc.material.pointSizeType = ForgePotree.PointSizeType.FIXED;
                }

                pc.material.rgbBrightness = (rgbBrightness / 100); // Standard brightness
                pc.material.rgbContrast = 0.8 * (rgbContrast / 100);   // Reduced contrast for less burning
                pc.material.rgbGamma = 1.2 * (rgbGamma / 100);       // Increased gamma for better saturation

                // Use point cloud's actual min/max elevations if available
                if (pc.minPointElevation !== undefined && pc.maxPointElevation !== undefined) {
                    // Apply user-defined range within actual bounds of the point cloud
                    const minElev = pc.minPointElevation;
                    const maxElev = pc.maxPointElevation;
                    const range = maxElev - minElev;

                    // Convert percentages to actual elevation values
                    const adjustedMin = minElev + (range * (elevationMin / 100));
                    const adjustedMax = minElev + (range * (elevationMax / 100));

                    pc.material.elevationRange = [adjustedMin, adjustedMax];

                    // Store the currently applied elevation range for debugging
                    pc.currentElevationRange = {
                        min: adjustedMin,
                        max: adjustedMax,
                        originalMin: minElev,
                        originalMax: maxElev
                    };
                } else {
                    // Fallback to default range if point cloud doesn't have elevation data
                    pc.material.elevationRange = [elevationMin, elevationMax];
                }

                pc.material.gradient = ForgePotree.Gradients[colorSchema];
            });
            // Route-scoped overrides win over global display settings while
            // their owning feature is active. The feature clears its entry on
            // teardown, so later viewers inherit the user's normal settings.
            this._pointCloudAppearanceOverrides?.forEach((settings, cloudName) => {
                this._applyPointCloudAppearance(cloudName, settings);
            });
            // Only cache after applying — prevents poisoning when no clouds loaded yet
            // (camera events fire during async loadPointCloud before any cloud exists)
            if (pointclouds.length > 0) {
                _prevPCSettings = { colorType, opacity, rgbBrightness, rgbContrast, rgbGamma,
                                    elevationMin, elevationMax, colorSchema, pointBudget,
                                    useEDL, pointSize, pointType, sizeType };
            }
        } else {
            // settings cache hit — material loop skipped (no work needed)
        }

        if (shownPointclouds.length > 0) {
            const camera = this.viewer.impl.camera; //.perspectiveCamera;
            const renderer = this.viewer.impl.glrenderer();
            ForgePotree.Global.maxNodesLoading = Math.min(3 + shownPointclouds.length, 8);
            ForgePotree.updatePointClouds(shownPointclouds, camera, renderer);

            this.viewer.impl.invalidate(false, false, true);

            // P1: safety-net restart — watcher should always be running while clouds are shown
            if (!this._loadingWatcherRafId) {
                this._startLoadingWatcher();
            }
        }
    }

    // P1: Loading-aware rAF watcher — replaces the 500ms blind setInterval.
    // Runs at rAF rate (60fps) while nodes stream, goes fully dormant when idle.
    _startLoadingWatcher() {
        if (this._loadingWatcherRafId) return; // already running
        this._loadingWatcherRafId = requestAnimationFrame(this._boundTickLoadingWatcher);
    }
    _tickLoadingWatcher() {
        // Stop only if all clouds have been removed
        if (!this._pointclouds || this._pointclouds.size === 0) {
            this._loadingWatcherRafId = null;
            return;
        }
        const now = performance.now();
        const timeSinceUpdate = now - this._lastUpdateTime;
        // Call every frame unless the camera event already fired this frame (14ms dedup).
        // No idle throttle — Potree queues the next download batch inside updatePointClouds(),
        // so any gap here directly delays loading. Settings cache makes idle calls nearly free.
        if (timeSinceUpdate > 14) {
            this._boundUpdatePointClouds();
        }

        // P10: sample frame interval for adaptive budget
        if (this._lastTickTime > 0) {
            var frameTime = now - this._lastTickTime;
            // Ignore outliers from tab switches or debugger pauses
            if (frameTime > 0 && frameTime < 200) {
                this._frameTimes.push(frameTime);
                if (this._frameTimes.length > 60) this._frameTimes.shift();
            }
        }
        this._lastTickTime = now;

        // P10: track how long loading has been idle (not just a momentary gap between batches)
        if (ForgePotree.Global.numNodesLoading > 0) {
            this._loadingIdleSince = 0; // reset — still loading
        } else if (this._loadingIdleSince === 0) {
            this._loadingIdleSince = now; // just became idle
        }

        // P10: adaptive budget adjustment — mode-aware settle detection
        if (now - this._lastAdaptiveCheck > 1000 && this._frameTimes.length >= 30) {
            var cv = this._computeFrameTimeCV();
            var shouldAdjust = false;

            if (this._maxDetailMode) {
                // Max Detail: only adjust after scene fully settles
                // Requires 5s network idle + consistent frame times (CV < 0.30)
                var isNetworkIdle = this._loadingIdleSince > 0 && (now - this._loadingIdleSince > 5000);
                shouldAdjust = isNetworkIdle && cv < 0.30;
            } else {
                // Smooth: adjust anytime frames are consistent (no idle requirement)
                shouldAdjust = cv < 0.30;
            }

            if (shouldAdjust) {
                this._lastAdaptiveCheck = now;
                this._adjustAdaptiveBudget();
            }
        }

        // P10: update FPS overlay display
        if (this._fpsMonitor && this._frameTimes.length >= 5) {
            var sum = 0;
            for (var i = 0; i < this._frameTimes.length; i++) sum += this._frameTimes[i];
            var avgMs = sum / this._frameTimes.length;
            var fps = avgMs > 0 ? Math.round(1000 / avgMs) : 0;
            var userBudget = (store.state.userSettings.displaySettings.pointCloudDisplay || {}).pointBudget || 1000000;
            var effectiveBudget = this._adaptiveBudget != null ? Math.min(this._adaptiveBudget, userBudget) : userBudget;
            var isAdapted = this._adaptiveBudget != null && this._adaptiveBudget < userBudget;
            this._fpsMonitor.update(fps, effectiveBudget, userBudget, isAdapted);
        }

        // Always reschedule — watcher lives as long as clouds are loaded
        this._loadingWatcherRafId = requestAnimationFrame(this._boundTickLoadingWatcher);
    }

    // P6: Scale pointLoadLimit based on actual scan point counts from metadata.
    // Called after each cloud is added or removed.
    _updatePointLoadLimit() {
        if (!this._pointclouds || this._pointclouds.size === 0) return;
        var rawTotal = 0;
        this._pointclouds.forEach(pc => {
            var scanPts = (pc.pcoGeometry && pc.pcoGeometry.numPoints) || 0;
            // Cap each cloud's contribution at 50M: LOD working set per view never
            // exceeds ~50M regardless of whether the scan has 5M or 3.5B total points.
            // Fallback to 50M if metadata didn't expose the count.
            rawTotal += scanPts > 0 ? Math.min(scanPts, 50000000) : 50000000;
        });
        // Floor  : 150M  — original baseline, never go lower
        // Buffer : ×1.5  — 50% headroom above working set prevents threshold thrashing
        // Ceiling: 300M  — hard cap (~8.4GB VRAM, safe for pro workstations)
        ForgePotree.Global.pointLoadLimit = Math.max(
            150000000,
            Math.min(rawTotal * 1.5, 300000000)
        );
        debugLog(`[P6] pointLoadLimit → ${(ForgePotree.Global.pointLoadLimit / 1e6).toFixed(0)}M pts (${this._pointclouds.size} cloud(s))`);
    }

    // P10: Coefficient of Variation of frame times.
    // Low CV (<0.30) = consistent frame times = steady-state rendering.
    // High CV (>0.30) = spiky frame times = loading/GPU upload jank (don't adapt).
    _computeFrameTimeCV() {
        var n = this._frameTimes.length;
        if (n < 10) return 1; // not enough data, assume unstable
        var sum = 0, sumSq = 0;
        for (var i = 0; i < n; i++) {
            sum += this._frameTimes[i];
            sumSq += this._frameTimes[i] * this._frameTimes[i];
        }
        var mean = sum / n;
        var variance = (sumSq / n) - (mean * mean);
        var stdDev = Math.sqrt(Math.max(0, variance));
        return mean > 0 ? stdDev / mean : 1;
    }

    // P10: Detect GPU tier from WebGL renderer string.
    // Sets download concurrency and GPU upload rate based on hardware capability.
    // Modifier x1 applied to all tiers for easy future tuning.
    _detectGPUTier() {
        if (this._gpuTier) return this._gpuTier;
        var modifier = 1; // easy to change later
        var renderer = '';
        try {
            var canvas = document.createElement('canvas');
            var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
                var ext = gl.getExtension('WEBGL_debug_renderer_info');
                if (ext) renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '';
            }
        } catch (e) { /* fallback to medium */ }

        // GPU classification by WebGL renderer string.
        // Each tier sets: download concurrency (parallel HTTP fetches) and
        // GPU upload rate (nodes pushed to VRAM per frame via toTreeNode).
        // Modifier × 1 applied for easy future tuning without changing thresholds.
        var tier, downloads, gpuPerFrame;

        if (/quadro|rtx\s*a[0-9]|a100|a6000/i.test(renderer)) {
            // Ultra: Quadro, RTX A-series (A4000, A6000), A100 — workstation GPUs
            tier = 'ultra'; downloads = 12; gpuPerFrame = 40;
        } else if (/rtx\s*(20[7-9]|30[6-9]|40[5-9]|309|408|409)/i.test(renderer)) {
            // High: RTX 2070+, RTX 3060+, RTX 4050+ — modern gaming GPUs
            tier = 'high'; downloads = 8; gpuPerFrame = 25;
        } else if (/gtx\s*(10[6-8]|16[5-8])|rx\s*(5[6-9]|6[6-9])/i.test(renderer)) {
            // Medium: GTX 1060-1080, GTX 1650-1680, AMD RX 560-690 — older discrete GPUs
            tier = 'medium'; downloads = 5; gpuPerFrame = 15;
        } else if (/intel|integrated|mesa|swiftshader|llvmpipe/i.test(renderer)) {
            // Low: Intel integrated, Mesa (Linux SW), SwiftShader (headless) — no discrete GPU
            tier = 'low'; downloads = 3; gpuPerFrame = 8;
        } else {
            // Unknown GPU — default to medium as a safe middle ground
            tier = 'medium'; downloads = 5; gpuPerFrame = 15;
        }

        this._gpuTier = {
            tier: tier,
            renderer: renderer,
            downloads: Math.round(downloads * modifier),
            gpuPerFrame: Math.round(gpuPerFrame * modifier)
        };
        // debugLog('[P10] GPU tier: ' + tier + ' (' + renderer + ') → ' + this._gpuTier.downloads + ' downloads, ' + this._gpuTier.gpuPerFrame + ' GPU/frame (modifier x' + modifier + ')');
        return this._gpuTier;
    }

    // P10: Apply loading speed based on GPU tier and boost mode.
    // Called on first cloud load and when boost toggle changes.
    _applyLoadingSpeed() {
        var tier = this._detectGPUTier();
        var boostMultiplier = 2; // doubled for faster high-LOD loading
        var dl = tier.downloads * (this._boostMode ? 2 * boostMultiplier : 1);
        var gpu = tier.gpuPerFrame * (this._boostMode ? 2 * boostMultiplier : 1);
        ForgePotree.setLoadingSpeed(dl, gpu);
        // debugLog('[P10] Loading speed applied: ' + dl + ' downloads, ' + gpu + ' GPU/frame (boost: ' + this._boostMode + ')');
    }

    // P10: Adaptive GPU budget — measures frame time and adjusts pointBudget
    // to maintain 30-50fps. User's slider value is the ceiling (never exceeded).
    _adjustAdaptiveBudget() {
        var sum = 0;
        for (var i = 0; i < this._frameTimes.length; i++) sum += this._frameTimes[i];
        var avg = sum / this._frameTimes.length;

        var userBudget = (store.state.userSettings.displaySettings.pointCloudDisplay || {}).pointBudget || 1000000;
        var current = this._adaptiveBudget != null ? this._adaptiveBudget : userBudget;
        var now = performance.now();

        if (avg > 33) {
            // Below 30fps — reduce budget (but NEVER in Max Detail mode)
            if (this._maxDetailMode) {
                // debugLog('[P10] GPU struggling (' + avg.toFixed(0) + 'ms/frame) but Max Detail ON → skip reduction');
            } else {
                this._adaptiveBudget = Math.max(1000000, current * 0.90);
                this._lastBudgetReduction = now;
                // debugLog('[P10] GPU struggling (' + avg.toFixed(0) + 'ms/frame) → budget ↓ ' + (this._adaptiveBudget / 1e6).toFixed(1) + 'M');
            }
        } else if (avg < 20 && current < userBudget && now - this._lastBudgetReduction > 5000) {
            // Above 50fps with headroom — increase by 10% toward user ceiling
            // Only if we haven't reduced in the last 5s (prevents oscillation)
            this._adaptiveBudget = Math.min(userBudget, current * 1.10);
            // debugLog('[P10] GPU has headroom (' + avg.toFixed(0) + 'ms/frame) → budget ↑ ' + (this._adaptiveBudget / 1e6).toFixed(1) + 'M');
        }
        // 20-33ms (30-50fps) → comfort zone, no change
    }

    onMouseUp(mouse) {
        let _this = store.state.forgeService.getMainExtension();
        if (_this.tempMesh)
            _this.viewer.impl.overlayScenes.pivot.scene.remove(_this.tempMesh)
        let closestPt = _this.pickNearestPointCloudPoint(mouse);
        if (!closestPt)
            return;
        const box = new THREE.BoxGeometry(.05, 0.05, 0.05);
        _this.tempMesh = new THREE.Mesh(
            box,
            new THREE.MeshBasicMaterial({ color: 0x0000ff })
        );
        _this.tempMesh.position.x = closestPt.x;
        _this.tempMesh.position.y = closestPt.y;
        _this.tempMesh.position.z = closestPt.z;
        this.viewer.impl.overlayScenes.pivot.scene.add(_this.tempMesh);
        this.viewer.impl.invalidate(true, true, true);
    }
    getVisiblePointClouds() {
        if (!this._pointCloudGroups) return [];
        return Array.from(this._pointCloudGroups.entries())
            .filter(([_, group]) => group.visible)
            .reduce((clouds, [name]) => {
                const pc = this._pointclouds.get(name);
                if (pc) {
                    clouds.push(...pc.visibleNodes.map(node => node.sceneNode));
                }
                return clouds;
            }, []);
    }
    pickNearestPointCloudPointWithRaycaster(raycaster) {
        const roughIntersects = this.quickRaycast(raycaster);
        if (!roughIntersects?.length) return null;

        let intersection = this.precisePick(raycaster, roughIntersects);

        return intersection;
    }

    pickNearestPointCloudPoint(mouseEvent) {
        let intersection = this.getMouseIntersectionPointCloud(mouseEvent.x, mouseEvent.y);
        return intersection;
    }

    precisePick(raycaster, pointclouds) {
        let intersections = [];
        pointclouds.forEach(({ node }) => {
            // intersectRecursively(raycaster, node, intersections)
            let intersects = raycaster.intersectObject(node, true);

            intersections.push(...intersects);

        })

        let minDistance = Infinity;
        let nearestPoint = null;

        let minDistanceToRay = Infinity;
        let minPointByDistanceToRay = null;

        for (let i = 0; i < intersections.length; i++) {
            // if (intersections[i].distance < minDistance) {
            //     minDistance = intersections[i].distance;
            //     nearestPoint = intersections[i].point;
            // }

            if (intersections[i].distanceToRay < minDistanceToRay) {
                minDistanceToRay = intersections[i].distanceToRay;
                minPointByDistanceToRay = intersections[i].point;
            }
        }

        return minPointByDistanceToRay;
    }

    quickRaycast(raycaster) {
        const candidates = this.getSpatialCandidates(raycaster);
        return candidates;
    }

    createEnhancedRaycaster(mouseEvent) {
        const raycaster = pointerToRaycaster(this.viewer, mouseEvent);
        raycaster.params.PointCloud.threshold = store.state.userSettings.displaySettings.pointCloudDisplay.pointSize ?? 8.0;
        return raycaster;
    }

    getSpatialCandidates(raycaster) {
        const candidates = [];
        const visited = new Set(); // Track visited nodes
        let nodes = this.getVisiblePointClouds();

        const visitNode = (node, parentPath = '') => {
            // Skip if node was already visited
            if (visited.has(node)) return;
            visited.add(node);

            // Check if node has geometry and bounding box
            if (!node.geometry || !node.geometry.boundingBox) return;

            if (!node.boundingBox) {
                node.boundingBox = node.geometry.boundingBox.clone();
                node.boundingBox.applyMatrix4(node.matrixWorld);
            }

            let intersectionPoint = new THREE.Vector3();

            // Check ray intersection with bounding box
            if (raycaster.ray.intersectsBox(node.boundingBox, intersectionPoint)) {
                // If this is a leaf node (no children) or has points, add it as candidate
                if (!node.children || node.children.length === 0) {
                    candidates.push({
                        node: node,
                        distance: node.boundingBox.distanceToPoint(raycaster.ray.origin),
                        path: parentPath + (parentPath ? '/' : '') + node.name
                    });
                }
                // Recursively visit children
                if (node.children) {
                    const nodePath = parentPath + (parentPath ? '/' : '') + (node.name || '');
                    node.children.forEach(child => visitNode(child, nodePath));
                }
            }
        };

        // Start recursive traversal for each root node
        nodes.forEach(node => visitNode(node));

        return candidates;
    }

    isWithinCropBox(viewerPoint){
        let extension = this.viewer.getExtension('Cropping3DExtension');
        if(extension.cropBox && extension.croppingOn)
            return extension.cropBox.containsPoint(viewerPoint);
        let croppingLevel = store.state.levels.find(o=>o.isCropped);
        if(croppingLevel){
            const bbox = croppingLevel.getViewCropBox();
            return viewerPoint.z <bbox.max.z &&viewerPoint.z >bbox.min.z;
        }
        return true;
    }
    updatePinPoint(){
        var point = store.state.pinnedPoint3d
        store.state.forgeService.clearElementsByClassName(this.pinClassName)
        if(point){
            var iconOptions = {
                position: point,
                differences: null,
                title: '',
                id: '',
                className: this.pinClassName,
                forgeViewer: this.viewer,
                onClick: () => { },
                addContent: false
            }
            store.state.forgeService.create3DIconToViewer(iconOptions);
            store.state.forgeService.updateLabelsPositionsByClassName(this.pinClassName, this.viewer)
        }
        store.state.forgeService.toggleCrossHairCursorToViewer(false, this.viewer)
        this.viewer.impl.invalidate(true,true,true)
    }

    //adding/editing tags
    getAllTaggedChildren(elements) {
        var list = elements;
        for (var i = 0; i < elements.length; i++) {
            if (elements[i].children == null)
                continue;
            var tagged = this.getAllTaggedChildren(elements[i].children);
            list = list.concat(tagged);
        }
        return list.filter(o => o.children == null);
    }
    async syncTagsToViewer() {
        if (!store.state.dataFetched || this.tagsMapped) return;

        debugLog(`%c Syncing tags to viewer`, 'color: #ff6600; font-weight: bold; font-size: 16px;');

        getLeafsObjectsAsync(this.viewer).then((leafsObjects) => {
            store.state.forgeService.allForgeElements = leafsObjects;

            if (store.state.userSettings.displaySettings) {
                // setModelVisibility(this.viewer, this.viewer.model, store.state.userSettings.displaySettings.showMasterModel);
                // setModelTransparency(this.viewer.model, store.state.userSettings.displaySettings.masterModelTransparency / 100);
            }
            this.tagsMapped = true;
        }).catch(error => {
            debugError('error', error)
        });
    }

    isolateDbIds(dbIds, model = null, ownerId = null) {
        if (!dbIds || dbIds.length == 0) {
            this.endIsolation(ownerId);
            return;
        }

        if (!this.viewer || !this.viewer.model) return;

        model ??= this.viewer.model;

        this.isolatedDbids = dbIds;
        isolateForge(this.viewer,model,dbIds)
        store.commit('setIsIsolating3D', true);

        if (store.state.isGhostingOn)
            store.state.forgeService.updateGhosting();
    }
    addToIsolate(dbIds, model = null) {
        this.isolatedDbids = this.isolatedDbids.concat(dbIds);
        this.isolateDbIds(this.isolatedDbids, model);
    }
    isolateDbIdsWithoutFitView(dbIds, model = null) {
        this.isolatedDbids = dbIds;
        store.state.forgeService.cameraZoomOutByScaler(0.25);
        this.viewer.isolate(dbIds, model);
        store.commit('setIsIsolating3D', true);
        store.state.forgeService.updateGhosting(false);
    }
    showDbIds(dbIds, model = null) {
        this.viewer.show(dbIds, model);
    }
    selectDbIds(dbIds){
        this.viewer.select(dbIds)
        this.viewer.utilities.fitToView();
    }
    /**
     * @param {string|null} ownerId the caller's claim on the SHARED CML marker layer, if any.
     */
    endIsolation(ownerId = null){
        this.isolatedDbids = [];
        if (this.viewer) {
            store.state.forgeService?.getScaffoldBoxExtension()?.clearIsolation();
        }
        store.state.forgeService.resetObjectsMaterials();
        // The CML marker layer is SHARED, and clearing it tears down the marker click handler, the
        // 360 hotspots and any open CML popup (forgeService.js:5113). A token set by a DIFFERENT
        // owner means those markers are not this caller's to destroy. An UNCLAIMED layer (null
        // token) keeps today's behaviour exactly, so every legacy de-isolate is unchanged.
        const cmlMarkerOwner = store.state.TAGS?.cmlMarkerOwner || null;
        if (!cmlMarkerOwner || cmlMarkerOwner === ownerId) {
            store.state.forgeService?.clearCmlPointMarkers();
        } else {
            debugLog('[mainExtension] endIsolation kept CML markers owned by', cmlMarkerOwner);
        }
        store.state.isGhostingOn = false;
        this.viewer.isolate([]);
        this.viewer.showAll();
        clearThemingColors(this.viewer);
        store.state.addRemove3dModeEnabled = false;
        store.state.forgeService.updateModelGray();
    }
    cropSelectedElements(){
        let box = getBBBox(this.viewer.model.selector.getSelection());
        let extension = this.viewer.getExtension('Cropping3DExtension');
        extension.cropBox = box;
        extension.toggleCropbox(true);
    }
    showDbIdsWithAnimation(dbIds, options) {
        options.totalTime = options.totalTime || 1000;
        options.intervals = options.intervals || 10;
        options.skipShow  = options.skipShow  || false;
        options.skipZoom  = options.skipZoom  || false;

        let guid = THREE.Math.generateUUID();

        store.commit('SET_LAST_TIME_LINE_PROCESS_ID', guid);

        const instanceTree = this.viewer.model.getInstanceTree();
        dbIds = dbIds.filter(id => instanceTree.isNodeHidden(id));

        const modelFrags = this.viewer.model.getFragmentList();

        let primitives = [];

        dbIds.forEach(dbId => {
            instanceTree.enumNodeChildren(dbId, child => {
                primitives.push(child)
            }, true)
        });

        primitives = primitives.filter(id => instanceTree.isNodeHidden(id));

        const fragments = getFragIdFromDbId(primitives);

        const materialManager = this.viewer.impl.matman();

        fragments.forEach((fragId) => {
            this.mappedMaterial[fragId] = modelFrags.getMaterial(fragId);

            if (!this.mappedMaterial[fragId]) {
                return;
            }

            let material = this.mappedMaterial[fragId].clone();

            materialManager.addMaterial(`${guid}-${fragId}`, material, true);

            this.viewer.model.unconsolidate();

            modelFrags.setMaterial(fragId, material);
        });

        let fragProxy = this.viewer.impl.getFragmentProxy(this.viewer.model, fragments);
        fragProxy.updateAnimTransform();

        let timeUnit = options.totalTime / options.intervals;

        let initialState = this.viewer.getState();
        initialState.objectSet = null;

        if (!options.skipShow) {
            setTimeout(() => {
                this.viewer.show(dbIds);
            }, timeUnit);
        }

        if (!options.skipZoom) {
            setTimeout(() => {
                this.viewer.fitToView(dbIds, this.viewer.model, false);
            }, timeUnit);

            // setTimeout(() => {
            //     if (store.getters['GET_LAST_TIME_LINE_PROCESS_ID'] === guid) {
            //         this.viewer.restoreState(initialState);
            //     }
            // }, options.totalTime)
        }

        setTimeout(() => {
            fragments.forEach((fragId) => {
                if (this.mappedMaterial[fragId])
                    modelFrags.setMaterial(fragId, this.mappedMaterial[fragId]);
            });

            this.viewer.impl.sceneUpdated(true);
        }, options.totalTime)

        for (let i = 1; i <= options.intervals; i++) {
            this.updateOpacity(fragments, i / options.intervals, i * timeUnit);
        }
    }
    updateOpacity(frags, opacity, delay) {
        setTimeout(() => {
            frags.forEach((fragId) => {
                let renderProxy = this.viewer.impl.getRenderProxy(this.viewer.model, [fragId]);
                if (renderProxy.material) renderProxy.material.opacity = opacity;
            });
            this.viewer.impl.sceneUpdated(true);
        }, delay);
    }
    getMouseIntersectionPointCloud(clientX, clientY) {
        if (!this._pointCloudGroups || this._pointCloudGroups.size === 0) {
            return null;
        }

        const viewer = this.viewer;
        const renderer = viewer.impl.glrenderer();
        const camera = viewer.impl.camera;

        const rect = viewer.impl.canvas.getBoundingClientRect();
        const x = (clientX - rect.left) * window.devicePixelRatio;
        const y = (clientY - rect.top) * window.devicePixelRatio;

        const raycaster = pointerToRaycaster(this.viewer, {
            clientX,
            clientY
        });

        const pointclouds = Array.from(this._pointclouds.values());
        const visiblePointclouds = pointclouds.filter(pc => pc.visible);

        if (visiblePointclouds.length === 0) {
            return null;
        }

        const params = {
            x: x,
            y: viewer.impl.canvas.height - y,
            width: viewer.impl.canvas.width,
            height: viewer.impl.canvas.height,
            pickClipped: true,
        };

        if (!store.state.forgeService.pRenderer) {
            store.state.forgeService.pRenderer = new Renderer(renderer)
        }

        const _viewer = {
            renderer: renderer,
            pRenderer: store.state.forgeService.pRenderer,
        }

        let intersections = [];

        for (const pointcloud of visiblePointclouds) {
            if (!pointcloud.pick) continue;

            const intersection = pointcloud.pick(_viewer, camera, raycaster.ray, params);

            if (intersection) {
                intersection.point = new THREE.Vector3(intersection.position.x, intersection.position.y, intersection.position.z);
                intersections.push(intersection);
            }
        }

        if (intersections.length === 0) {
            return null;
        }

        // Calculate the closest point to the ray origin
        let minDistance = Infinity;
        let closestPoint = null;
        intersections.forEach(intersection => {
            const distance = intersection.point.distanceTo(raycaster.ray.origin);
            if (distance < minDistance) {
                minDistance = distance;
                closestPoint = intersection.point;
            }
        });

        return closestPoint;
    }
}
Autodesk.Viewing.theExtensionManager.registerExtension('MainExtension', MainExtension);
